import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BellIcon } from '@heroicons/react/24/outline';
import ZoneNotificationSettings from './ZoneNotificationSettings';
import NotificationDeliveryTable from './NotificationDeliveryTable';

const GLOBAL_ZONE_NAME = '_global';

interface NotificationSettingsProps {
  onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

/**
 * NotificationSettings component for managing how registration/governance
 * events get surfaced to admins/approvers: in-app badge, webhook(s), and
 * email (AGENTGOV-136).
 *
 * Settings are per-zone (the same team/department boundary used elsewhere
 * in the registry) plus a reserved "_global" fallback used for anything not
 * in a specific zone. Saved settings take effect immediately -- no restart.
 */
const NotificationSettings: React.FC<NotificationSettingsProps> = ({ onShowToast }) => {
  const [zones, setZones] = useState<string[]>([]);
  const [selectedZone, setSelectedZone] = useState<string>(GLOBAL_ZONE_NAME);
  const [zonesLoading, setZonesLoading] = useState(true);

  useEffect(() => {
    axios
      .get<{ zones: string[] }>('/api/notifications/zones')
      .then((response) => setZones(response.data.zones))
      .catch(() => setZones([]))
      .finally(() => setZonesLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <BellIcon className="h-6 w-6" />
          Notifications
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Control how approvers find out about things waiting for a decision, and how
          registration/lifecycle events get announced. "Global" applies to anything not covered
          by a more specific zone below.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Zone
        </label>
        <select
          value={selectedZone}
          onChange={(e) => setSelectedZone(e.target.value)}
          disabled={zonesLoading}
          className="w-full md:w-80 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value={GLOBAL_ZONE_NAME}>Global (default)</option>
          {zones.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Each zone can have its own webhook and email settings. A zone with no settings of its
          own falls back to Global.
        </p>
      </div>

      <ZoneNotificationSettings
        key={selectedZone}
        zoneName={selectedZone}
        onShowToast={onShowToast}
      />

      <NotificationDeliveryTable />
    </div>
  );
};

export default NotificationSettings;
